import type { ExtendedGenerationRecord } from 'dynamo-daos';

import type { MotionRecord } from '@cartwhl/common/api/getMotionsByGroup';
import getProjectHistory from '@cartwhl/common/api/getProjectHistory';

import { fetchAllPaginated } from './assetLibraryAbortableLoader';
import type { AssetLibraryGroupDetailItem } from './assetLibraryGroupDetail';
import { PROJECT_HISTORY_PAGE_SIZE } from './assetLibraryGroupIds';

export type ProjectGenerationRecord = ExtendedGenerationRecord & {
  title?: string;
  studioPreviewURL?: string;
};

export type MappedProjectGeneration = {
  detailItem: AssetLibraryGroupDetailItem | null;
  motionRecord: MotionRecord | null;
};

function toIsoTimestamp(value: string | Date | undefined): string {
  if (!value) {
    return new Date(0).toISOString();
  }

  if (typeof value === 'string') {
    return value;
  }

  return value.toISOString();
}

function isCompletedProjectGeneration(
  generation: ProjectGenerationRecord,
): boolean {
  return generation.status === 'COMPLETED';
}

export function mapProjectGeneration(
  generation: ProjectGenerationRecord,
): MappedProjectGeneration {
  if (!isCompletedProjectGeneration(generation)) {
    return { detailItem: null, motionRecord: null };
  }

  const previewURL = generation.studioPreviewURL?.trim() || null;
  const bvhURL = generation.bvhURL?.trim();
  const name = generation.title?.trim() || generation.jobID;

  const detailItem: AssetLibraryGroupDetailItem | null = previewURL
    ? {
        id: generation.jobID,
        name,
        previewURL,
        createdAt: generation.createdAt,
        createdBy: generation.createdBy,
      }
    : null;

  const motionRecord: MotionRecord | null = bvhURL
    ? {
        jobID: generation.jobID,
        status: 'COMPLETED',
        bvhURL,
        studioPreviewURL: previewURL || '',
        title: name,
        createdBy: generation.createdBy,
        createdAt: toIsoTimestamp(generation.createdAt),
        lastUpdatedAt: toIsoTimestamp(generation.lastUpdatedAt),
      }
    : null;

  return { detailItem, motionRecord };
}

export async function fetchProjectHistoryPage(
  projectID: string,
  nextToken?: string,
): Promise<{
  detailItems: AssetLibraryGroupDetailItem[];
  projectMotions: MotionRecord[];
  nextToken?: string;
}> {
  const { data, error } = await getProjectHistory(
    projectID,
    PROJECT_HISTORY_PAGE_SIZE,
    nextToken,
  );

  if (error || !data) {
    throw new Error(error?.message || 'Unable to load project motions.');
  }

  const detailItems: AssetLibraryGroupDetailItem[] = [];
  const projectMotions: MotionRecord[] = [];

  for (const generation of data.generationHistory ?? []) {
    const mapped = mapProjectGeneration(generation);
    if (mapped.detailItem) {
      detailItems.push(mapped.detailItem);
    }
    if (mapped.motionRecord) {
      projectMotions.push(mapped.motionRecord);
    }
  }

  return {
    detailItems,
    projectMotions,
    nextToken: data.nextToken,
  };
}

export async function fetchAllProjectMotions(
  projectID: string,
  options: {
    initialMotions?: MotionRecord[];
    initialNextToken?: string;
  } = {},
): Promise<MotionRecord[]> {
  return fetchAllPaginated({
    initialItems: options.initialMotions ?? [],
    initialNextToken: options.initialNextToken,
    fetchPage: async (nextToken) => {
      const page = await fetchProjectHistoryPage(projectID, nextToken);
      return {
        items: page.projectMotions,
        nextToken: page.nextToken,
      };
    },
  });
}
